/**
 * Domestic request wizard shell — ported from V2's RequestWizard, trimmed to the one
 * flow V3 ships.
 *
 * V2's RequestWizard also owned country selection (via CountrySelector), routed USA to
 * the domestic wizard and everything else to the international wizard, and guarded route
 * changes with useWizardGuard. In V3 the App stage machine already picked the country
 * before this renders, there is no international path yet, and there is no router — so
 * this shell keeps only the sticky header, step indicator, per-step validation gate and
 * sticky footer around the domestic wizard. The presentational chrome is V2 verbatim.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Button } from '@/fluent-adapter';
import { Card, CardContent } from '@/fluent-adapter';
import { makeStyles, tokens, Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions, Button as FluentButton } from '@fluentui/react-components';
import { ArrowLeft24Regular as ArrowLeft, ArrowRight24Regular as ArrowRight, Dismiss24Regular as X } from '@fluentui/react-icons';
import { StepIndicator, getDomesticSteps } from '../StepIndicator/StepIndicator';
import { DomesticWizard } from '../DomesticRequest/DomesticWizard';
import { useToast } from '@/fluent-adapter';
import { useAppDispatch, useAppSelector } from '@/store';
import { resetWizard as resetDomesticWizard, selectDomesticWizard, setValidationErrors as setDomesticValidationErrors } from '../../store/domesticWizardSlice';
import { validateDomesticStep, scrollToFirstError, type ValidationResult } from '../../utils/validation';

interface RequestWizardProps {
  countryName: string;
  onExit: () => void;
}

const useStyles = makeStyles({
  container: {
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: 'hidden',
  },
  stickyHeader: {
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    zIndex: 10,
    paddingTop: tokens.spacingVerticalXL,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    '@media (max-width: 768px)': {
      paddingTop: tokens.spacingVerticalM,
      paddingLeft: tokens.spacingHorizontalM,
      paddingRight: tokens.spacingHorizontalM,
    },
  },
  headerInner: {
    maxWidth: '1120px',
    marginLeft: 'auto',
    marginRight: 'auto',
    width: '100%',
    '@media (max-width: 768px)': {
      paddingLeft: tokens.spacingHorizontalM,
      paddingRight: tokens.spacingHorizontalM,
    },
  },
  headerBlock: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    rowGap: tokens.spacingVerticalS,
    columnGap: tokens.spacingHorizontalL,
    flexWrap: 'wrap',
    marginBottom: tokens.spacingVerticalL,
  },
  headerTitle: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightSemibold,
    margin: 0,
  },
  headerMeta: {
    marginTop: tokens.spacingVerticalXS,
    color: tokens.colorNeutralForeground3,
  },
  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
    paddingTop: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    '@media (max-width: 768px)': {
      paddingTop: tokens.spacingVerticalM,
      paddingBottom: tokens.spacingVerticalM,
      paddingLeft: tokens.spacingHorizontalM,
      paddingRight: tokens.spacingHorizontalM,
    },
  },
  contentInner: {
    maxWidth: '1120px',
    marginLeft: 'auto',
    marginRight: 'auto',
    width: '100%',
    '@media (max-width: 768px)': {
      paddingLeft: tokens.spacingHorizontalM,
      paddingRight: tokens.spacingHorizontalM,
    },
  },
  stepFocusAnchor: {
    outline: 'none',
  },
  stickyFooter: {
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    '@media (max-width: 768px)': {
      paddingLeft: tokens.spacingHorizontalS,
      paddingRight: tokens.spacingHorizontalS,
    },
  },
  footerInner: {
    maxWidth: '1120px',
    marginLeft: 'auto',
    marginRight: 'auto',
    width: '100%',
  },
  navRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: tokens.spacingHorizontalL,
    flexWrap: 'nowrap',
    '@media (max-width: 768px)': {
      columnGap: tokens.spacingHorizontalXS,
    },
  },
  stepStatus: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightMedium,
    flex: '1 1 auto',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    minWidth: 0,
    '@media (max-width: 768px)': {
      fontSize: tokens.fontSizeBase200,
      flex: '0 1 auto',
    },
  },
  navButtonGroup: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
  },
  nextIcon: { display: 'inline-flex', marginLeft: tokens.spacingHorizontalXS },
  prevIcon: { display: 'inline-flex', marginRight: tokens.spacingHorizontalXS },
  buttonTextMobile: {
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
  buttonIconOnly: {
    '@media (max-width: 768px)': {
      marginLeft: 0,
      marginRight: 0,
    },
  },
});

export const RequestWizard: React.FC<RequestWizardProps> = ({ countryName, onExit }) => {
  const styles = useStyles();
  const dispatch = useAppDispatch();
  const { toast } = useToast();

  const domesticWizardState = useAppSelector(selectDomesticWizard);

  const [currentStep, setCurrentStep] = useState(0);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const stepFocusRef = useRef<HTMLDivElement>(null);
  const hasNavigatedRef = useRef(false);

  useEffect(() => {
    const mainEl = document.querySelector('main');
    if (mainEl) mainEl.scrollTop = 0;
    if (!hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      return;
    }
    const anchor = stepFocusRef.current;
    if (!anchor) return;
    anchor.focus({ preventScroll: true });
    anchor.scrollIntoView({ block: 'start' });
  }, [currentStep]);

  const steps = React.useMemo(() => getDomesticSteps(), []);
  const totalSteps = 7;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const validationResult: ValidationResult = validateDomesticStep(currentStep, {
        typeOfRequest: domesticWizardState.typeOfRequest,
        natureOfCrime: domesticWizardState.natureOfCrime,
        ndo: domesticWizardState.ndo,
        agentInfo: domesticWizardState.agentInfo,
        typeOfService: domesticWizardState.typeOfService,
        fileUpload: domesticWizardState.fileUpload,
        review: domesticWizardState.review,
      });

      if (!validationResult.isValid) {
        dispatch(setDomesticValidationErrors(validationResult.errors));
        scrollToFirstError(validationResult.errors);
        toast({
          title: 'Validation Error',
          description: 'Please fill in all required fields before proceeding.',
        });
        return;
      }

      dispatch(setDomesticValidationErrors({}));
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepIndex: number) => {
    if (stepIndex <= currentStep) {
      setCurrentStep(stepIndex);
    }
  };

  const handleConfirmLeave = () => {
    dispatch(resetDomesticWizard());
    setShowLeaveDialog(false);
    onExit();
  };

  const handleSubmitted = useCallback(() => {
    onExit();
  }, [onExit]);

  const isLastStep = currentStep === totalSteps - 1;
  const isFirstStep = currentStep === 0;

  return (
    <div className={styles.container}>
      {/* Sticky Header with Title and Step Indicator */}
      <div className={styles.stickyHeader}>
        <div className={styles.headerInner}>
          <div className={styles.headerBlock}>
            <div style={{ minWidth: 0 }}>
              <h1 className={styles.headerTitle}>
                Data Request
              </h1>
              <p className={styles.headerMeta}>
                Country / Region: <span style={{ fontWeight: tokens.fontWeightSemibold }}>{countryName}</span>
              </p>
            </div>
            <div className={styles.navButtonGroup}>
              <Button variant="outline" onClick={() => setShowLeaveDialog(true)} size="sm">
                <span className={styles.prevIcon}><X /></span>
                Cancel
              </Button>
            </div>
          </div>
          <StepIndicator
            steps={steps}
            currentStep={currentStep}
            onStepClick={handleStepClick}
          />
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div className={styles.scrollableContent}>
        <div className={styles.contentInner}>
          <div
            ref={stepFocusRef}
            tabIndex={-1}
            aria-label={`Step ${currentStep + 1} of ${totalSteps}${steps[currentStep]?.title ? `: ${steps[currentStep].title}` : ''}`}
            className={styles.stepFocusAnchor}
          >
            <Card>
              <CardContent className="p-6">
                <DomesticWizard
                  currentStep={currentStep}
                  onSubmitted={handleSubmitted}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Sticky Footer with Navigation Buttons */}
      <div className={styles.stickyFooter}>
        <div className={styles.footerInner}>
          {!isLastStep && (
            <div className={styles.navRow}>
              <Button variant="outline" onClick={handlePrevious} disabled={isFirstStep}>
                <span className={`${styles.prevIcon} ${styles.buttonIconOnly}`}><ArrowLeft /></span>
                <span className={styles.buttonTextMobile}>Previous</span>
              </Button>
              <div className={styles.stepStatus}>Step {currentStep + 1} of {totalSteps}</div>
              <Button onClick={handleNext}>
                <span className={styles.buttonTextMobile}>Next</span>
                <span className={`${styles.nextIcon} ${styles.buttonIconOnly}`}><ArrowRight /></span>
              </Button>
            </div>
          )}
          {isLastStep && (
            <div className={styles.navRow}>
              <Button variant="outline" onClick={handlePrevious}>
                <span className={`${styles.prevIcon} ${styles.buttonIconOnly}`}><ArrowLeft /></span>
                <span className={styles.buttonTextMobile}>Previous</span>
              </Button>
              <div className={styles.stepStatus}>Step {currentStep + 1} of {totalSteps}</div>
              <div>{/* Placeholder for layout balance; the review step owns Submit */}</div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={showLeaveDialog} onOpenChange={(_, data) => setShowLeaveDialog(!!data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Leave Wizard</DialogTitle>
            <DialogContent>
              You will lose progress if you leave the wizard
            </DialogContent>
            <DialogActions>
              <FluentButton appearance="secondary" onClick={handleConfirmLeave}>Leave</FluentButton>
              <FluentButton appearance="primary" onClick={() => setShowLeaveDialog(false)}>Stay</FluentButton>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};
